// Location add/edit page.
//
// Add mode includes an optional "Linked Access Point" dropdown above the
// 3D Position section. Picking an existing AP:
//   - prefills the location's X / Y / Z from the AP's coordinates
//   - on save, re-parents that AP to the newly created location
//     (UPDATE access_points SET location_id = newId WHERE id = pickedAp)

import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {ChipSelector} from '../../components/common/ChipSelector'
import {FormField} from '../../components/common/FormField'
import {SelectField} from '../../components/common/SelectField'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {showToast} from '../../components/common/Toast'
import {supabase, DEFAULT_MINE_ID} from '../../app/supabaseClient'
import {
  LOCATION_HEADING_STATUSES,
  LOCATION_MATERIALS,
  LOCATION_PRIORITIES,
  LOCATION_TYPES,
  MINE_LEVELS,
  generateId,
} from '../../lib/constants'
import type {LocationDetail, LocationPriority, LocationType} from './types'

// locations.level is INT, so we store the numeric part as the option value
// (e.g. value '1232' for label 'Level 1232'). The empty value maps to null
// on save.
const LEVEL_OPTIONS = [
  {label: '— None —', value: ''},
  ...MINE_LEVELS.map(l => {
    const num = l.replace('Level_', '')
    return {label: `Level ${num}`, value: num}
  }),
]

interface APOption {
  id: string
  name: string
  x: number | null
  y: number | null
  z: number | null
}

interface FormErrors {
  name?: string
  zone?: string
}

function parseNumeric(s: string): number | null {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export const LocationFormScreen: React.FC = () => {
  const {id} = useParams<{id?: string}>()
  const navigate = useNavigate()
  const isEdit = Boolean(id)

  const [loading, setLoading] = useState(isEdit)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})

  const [name, setName] = useState('')
  const [zone, setZone] = useState(isEdit ? '' : 'VS')
  const [description, setDescription] = useState('')
  const [type, setType] = useState<LocationType | null>(null)
  const [priority, setPriority] = useState<LocationPriority | null>(isEdit ? null : 'MEDIUM')
  const [material, setMaterial] = useState<string | null>(null)
  const [headingStatus, setHeadingStatus] = useState<string | null>(null)
  const [tonnesRemaining, setTonnesRemaining] = useState('')
  const [x, setX] = useState('')
  const [y, setY] = useState('')
  const [z, setZ] = useState('')
  const [level, setLevel] = useState('')

  // Optional AP linkage — add mode only. The picked AP's id; empty string = none.
  const [aps, setAPs] = useState<APOption[]>([])
  const [linkedAPId, setLinkedAPId] = useState('')

  // Fetch APs once on mount so the dropdown can offer them.
  useEffect(() => {
    if (isEdit) return
    let cancelled = false
    ;(async () => {
      const {data} = await supabase
        .from('access_points')
        .select('id, name, x, y, z')
        .order('name')
      if (!cancelled) setAPs((data ?? []) as APOption[])
    })()
    return () => {
      cancelled = true
    }
  }, [isEdit])

  useEffect(() => {
    if (!isEdit || !id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const {data, error} = await supabase
        .from('locations')
        .select(
          'id, name, zone, level, type, description, priority, material, heading_status, ' +
            'next_blast_date, tonnes_remaining, x, y, z',
        )
        .eq('id', id)
        .single<LocationDetail>()
      if (cancelled) return
      if (error || !data) {
        showToast(`Load failed: ${error?.message ?? 'Not found'}`, 'error')
        navigate('/locations')
        return
      }
      setName(data.name)
      setZone(data.zone ?? '')
      setDescription(data.description ?? '')
      setType(data.type)
      setPriority(data.priority)
      setMaterial(data.material)
      setHeadingStatus(data.heading_status)
      setTonnesRemaining(data.tonnes_remaining != null ? String(data.tonnes_remaining) : '')
      setX(data.x != null ? String(data.x) : '')
      setY(data.y != null ? String(data.y) : '')
      setZ(data.z != null ? String(data.z) : '')
      setLevel(data.level != null ? String(data.level) : '')
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id, isEdit, navigate])

  const validate = (): boolean => {
    const e: FormErrors = {}
    if (!name.trim()) e.name = 'Location name is required'
    if (!zone.trim()) e.zone = 'Zone is required'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSave = async () => {
    if (!validate()) return
    setBusy(true)
    const now = new Date().toISOString()
    const payload = {
      name: name.trim(),
      zone: zone.trim(),
      description: description.trim() || null,
      type,
      priority,
      material,
      heading_status: headingStatus,
      tonnes_remaining: parseNumeric(tonnesRemaining),
      x: parseNumeric(x),
      y: parseNumeric(y),
      z: parseNumeric(z),
      level: parseNumeric(level),
      updated_at: now,
    }
    if (!isEdit) {
      const newId = generateId()
      const {error} = await supabase.from('locations').insert({
        id: newId,
        mine_id: DEFAULT_MINE_ID,
        ...payload,
        created_at: now,
      })
      if (error) {
        showToast(`Add failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }

      // Optional: re-parent the picked AP to the new location. Independent of
      // the location insert — if this fails we surface a toast but keep the
      // location, since the user can fix the AP link via the detail screen.
      if (linkedAPId) {
        const {error: apErr} = await supabase
          .from('access_points')
          .update({location_id: newId, updated_at: now})
          .eq('id', linkedAPId)
        if (apErr) {
          showToast(`Location saved but linking AP failed: ${apErr.message}`, 'error')
        } else {
          showToast('Location added and AP linked.', 'success')
        }
      } else {
        showToast('Location added.', 'success')
      }
      setBusy(false)
      navigate('/locations')
      return
    } else {
      const {error} = await supabase.from('locations').update(payload).eq('id', id!)
      if (error) {
        showToast(`Update failed: ${error.message}`, 'error')
        setBusy(false)
        return
      }
      showToast('Location updated.', 'success')
    }
    setBusy(false)
    navigate('/locations')
  }

  const handleDelete = async () => {
    if (!id) return
    if (!window.confirm(`Delete location "${name}"? Child access points and events will also be removed.`)) {
      return
    }
    setBusy(true)
    await supabase.from('location_events').delete().eq('location_id', id)
    await supabase.from('access_points').delete().eq('location_id', id)
    const {error} = await supabase.from('locations').delete().eq('id', id)
    setBusy(false)
    if (error) {
      showToast(`Delete failed: ${error.message}`, 'error')
      return
    }
    showToast('Location deleted.', 'success')
    navigate('/locations')
  }

  const title = isEdit ? `Edit ${name || 'Location'}` : 'Add Location'
  const saveLabel = isEdit ? 'Save Changes' : 'Add Location'

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header title={title} backTo="/locations" />
      <PageShell
        footer={
          <>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={busy}
                className="mr-auto rounded-md border border-statusRed px-lg py-sm text-md text-statusRed hover:bg-statusRed hover:text-white disabled:opacity-40"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate('/locations')}
              disabled={busy}
              className="rounded-md border border-surfaceBorder px-lg py-sm text-md text-textSecondary hover:border-textPrimary hover:text-textPrimary disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              className="rounded-md bg-primary px-lg py-sm text-md font-bold text-black hover:bg-primaryLight disabled:opacity-40"
            >
              {busy ? 'Saving…' : saveLabel}
            </button>
          </>
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : (
          <div className="flex flex-col gap-lg">
            <div className="flex gap-lg">
              <FormField
                label="Name"
                value={name}
                onChange={setName}
                placeholder="e.g. VS_FFS_700-1400_CON/STL"
                error={errors.name ?? null}
              />
              <FormField
                label="Zone"
                value={zone}
                onChange={setZone}
                placeholder="e.g. VS / MS"
                error={errors.zone ?? null}
              />
            </div>
            <FormField
              label="Description"
              value={description}
              onChange={setDescription}
              placeholder="Optional notes"
            />

            <ChipSelector label="Type" options={LOCATION_TYPES} value={type} onChange={setType} />
            <ChipSelector
              label="Priority"
              options={LOCATION_PRIORITIES}
              value={priority}
              onChange={setPriority}
            />
            <ChipSelector
              label="Material"
              options={LOCATION_MATERIALS}
              value={material as any}
              onChange={setMaterial}
            />
            <ChipSelector
              label="Heading Status"
              options={LOCATION_HEADING_STATUSES}
              value={headingStatus as any}
              onChange={setHeadingStatus}
            />

            <div className="flex gap-lg">
              <FormField
                label="Tonnes Remaining"
                value={tonnesRemaining}
                onChange={setTonnesRemaining}
                inputMode="decimal"
              />
            </div>

            {/* Linked Access Point — add mode only. Optional. Picking an AP
                prefills the location's X / Y / Z from the AP and, on save,
                re-parents the AP to this location. */}
            {!isEdit && (
              <SelectField
                label="Access Point (optional)"
                value={linkedAPId}
                onChange={v => {
                  setLinkedAPId(v)
                  if (!v) return
                  const picked = aps.find(a => a.id === v)
                  if (!picked) return
                  if (picked.x != null) setX(String(picked.x))
                  if (picked.y != null) setY(String(picked.y))
                  if (picked.z != null) setZ(String(picked.z))
                }}
                options={[
                  {label: '— None —', value: ''},
                  ...aps.map(a => ({label: a.name, value: a.id})),
                ]}
              />
            )}

            <div className="text-sm font-semibold uppercase tracking-wider text-textMuted">
              3D Position (optional)
            </div>
            <div className="flex gap-lg">
              <FormField label="X" value={x} onChange={setX} placeholder="0.0" />
              <FormField label="Y" value={y} onChange={setY} placeholder="0.0" />
              <FormField label="Z" value={z} onChange={setZ} placeholder="0.0" />
              <SelectField
                label="Level"
                value={level}
                onChange={setLevel}
                options={LEVEL_OPTIONS}
              />
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}
